import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false });

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        destructive: !!opts.destructive,
        resolve,
      });
    });
  }, []);

  const close = useCallback((value) => {
    setState((s) => {
      s.resolve?.(value);
      return { ...s, open: false };
    });
  }, []);

  const ctxValue = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmCtx.Provider value={ctxValue}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => !o && close(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            {state.description && <AlertDialogDescription>{state.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" onClick={() => close(false)}>
              {state.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-action"
              onClick={() => close(true)}
              className={state.destructive ? "bg-rose-500 hover:bg-rose-600 text-white" : "bg-orange-500 hover:bg-orange-600 text-white"}
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmCtx).confirm;
